/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import React, {
	ReactNode,
	useEffect,
	useState,
} from 'react';

// Style
import classes from './DateFilter.module.css';

// Mantine
import {
	Popover,
	Box,
	Stack,
	Button,
	Text,
	Image,
	ActionIcon,
	rem,
} from '@mantine/core';
import {
	useClickOutside,
	useMediaQuery,
} from '@mantine/hooks';

// Datepicker component
import {
	DatePickerInput,
	DatePickerProps,
} from '@mantine/dates';
import dayjs from 'dayjs';

// Icons
import CheckIcon from '@assets/icons/check-filter.svg';
import ArrowDownIcon from '@assets/icons/arrow-down.svg';
import CalendarIcon from '@assets/icons/calendar.svg';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@utils/functions';

// Type
type Item = {
	label: string;
	value: any;
};

const options = [
	{
		id: 'OP0k61R0_ZHjdHi61-date',
		label: 'global.allData',
		value: [null, null],
	},
	{
		id: 'OP0k61R0_ZHjdHi62-date',
		label: 'global.thisMonth',
		value: [
			dayjs().startOf('month'),
			dayjs().endOf('month'),
		],
	},
	{
		id: 'OP0k61R0_ZHjdHi63-date',
		label: 'global.thisYear',
		value: [
			dayjs().startOf('year'),
			dayjs().endOf('year'),
		],
	},
];

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DateFilter = (props: {
	onApply: (item: Item[]) => void;
	onChange?: (item: Item) => void;
	children?: ReactNode;
	size?: string;
	variant?: string;
	placeholder?: string;
	color?: string;
	label?: string;
	options?: Item[];
	fullWidth?: boolean;
	loading?: boolean;
	disabled?: boolean;
	opened?: boolean;
	leftSection?: React.ReactNode;
	init?: 'month' | 'allData' | 'year';
}) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	/* ******************************** States ******************************* */
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [formatedDate, setFormatedDate] =
		useState<any>(null);
	const [rangeDate, setRangeDate] = useState<
		[Date | null, Date | null]
	>([null, null]);

	const [appliedRangeDate, setAppliedRangeDate] =
		useState<[Date | null, Date | null]>([null, null]);

	const [selectedItems, setSelectedItems] = useState<
		Item[]
	>([{ label: 'Date filter', value: dayjs() }]);

	const [appliedItems, setAppliedItems] = useState<
		Item[]
	>([{ label: 'Date filter', value: dayjs() }]);

	const icon = (
		<Image
			style={{ width: rem(18), height: rem(18) }}
			src={CalendarIcon}
		/>
	);

	const IS_DISABLED =
		selectedItems.some(
			(selectedItem) => selectedItem.label === 'custom'
		) &&
		(rangeDate[0] == null || rangeDate[1] == null);

	/* ******************************** Helpers ******************************* */
	const handleRemoveItems = () => {
		if (appliedItems[0].label !== 'custom') {
			setRangeDate([null, null]);
		} else {
			setRangeDate(appliedRangeDate);
		}

		setSelectedItems(appliedItems);
	};

	const refDropdown = useClickOutside(handleRemoveItems);

	useEffect(() => {
		if (props.init) {
			switch (props.init) {
				case 'allData':
					setSelectedItems([]);
					handleCheckboxChange(options[0]);
					setAppliedItems([options[0]]);
					break;
				case 'month':
					setSelectedItems([]);
					handleCheckboxChange(options[1]);
					setAppliedItems([options[1]]);
					break;
				case 'year':
					setSelectedItems([]);
					handleCheckboxChange(options[2]);
					setAppliedItems([options[2]]);

					break;
				default:
					break;
			}
		}
	}, []);

	const handleApply = () => {
		const updatedItems = [...selectedItems].map(
			(item) => ({ ...item })
		);

		setAppliedItems(updatedItems);
		setSelectedItems(updatedItems);
		setAppliedRangeDate(rangeDate);
		let newBody = null;
		if (
			selectedItems &&
			Array.isArray(selectedItems) &&
			selectedItems[0].label == 'custom'
		) {
			newBody = [
				{
					label: selectedItems[0].label,
					value: formatedDate,
				},
			];

			props.onApply(newBody);
			return;
		}

		props.onApply(selectedItems);
	};

	const handleCheckboxChange = (item: Item) => {
		const isSelected = selectedItems.some(
			(selectedItem) => selectedItem.value === item.value
		);
		if (isSelected) {
			setSelectedItems([
				{ label: 'Date filter', value: dayjs() },
			]);
		} else {
			setRangeDate([null, null]);
			setSelectedItems([item]);
		}
	};

	const handleCheckboxDateRangeChange = (item: Item) => {
		if (selectedItems[0].label == 'custom') {
			setRangeDate([null, null]);
			setSelectedItems([
				{ label: 'Date filter', value: dayjs() },
			]);
		} else {
			setSelectedItems([item]);
		}
	};

	/**
	 * Returns the properties for the specified day in the DatePicker calendar.
	 * @param {Date} date - The date of the day for which to retrieve the properties.
	 * @returns {Object} The properties of the specified day in the DatePicker calendar, including background style.
	 */
	const getDayProps: DatePickerProps['getDayProps'] = (
		date
	) => {
		if (
			dayjs(date).format('YYYY-MM-DD') ==
			dayjs().format('YYYY-MM-DD')
		) {
			return {
				style: {
					backgroundColor: '#E4E7FF',
				},
			};
		}

		return {};
	};

	const getDatesSelected = (
		selectedItems: { label: string; value: string[] }[]
	): string | undefined => {
		if (!selectedItems?.length) return undefined;

		const [firstItem] = selectedItems;

		if (firstItem.label === 'Date filter') {
			return 'Date';
		}

		if (firstItem.label === 'global.allData') {
			return t('global.allData');
		}

		const [startDate, endDate] = firstItem.value;

		return `${formatDate(startDate, 'DD/MM/YYYY')} - ${formatDate(endDate, 'DD/MM/YYYY')}`;
	};
	/* ******************************** RENDERING ******************************* */

	return (
		<Popover
			width={'target'}
			position="bottom"
			classNames={classes}
			clickOutsideEvents={['mouseup', 'touchend']}
			disabled={props.disabled}
			opened={props.opened}
		>
			<Popover.Target>
				<Box
					data-testid="M00k61R0_ZHjdHi6B-button"
					onClick={() => {
						setOpen(!open);
					}}
					className={clsx(
						!props.disabled
							? classes.containerTarget &&
								props.size === 'sm'
								? classes.containerSmallTarget
								: classes.containerTarget
							: classes.containerTargetDisabled,
						selectedItems &&
							selectedItems[0].label !== 'Date filter' &&
							classes.containerTargetSelected
					)}
					variant="outline"
				>
					{selectedItems[0] &&
						getDatesSelected(selectedItems)}
					<ActionIcon size={'12px'} variant="transparent">
						<Image
							className={clsx(
								classes.containerIconUp,
								props.disabled &&
									classes.containerIconUpDisabled
							)}
							src={ArrowDownIcon}
						/>
					</ActionIcon>
				</Box>
			</Popover.Target>
			<Popover.Dropdown ref={refDropdown}>
				<Stack gap={'0px'}>
					{props &&
					options &&
					Array.isArray(options) &&
					options.length > 0 ? (
						options.map((item: any) => (
							<Box
								data-testid={item.id}
								key={item.id}
								className={classes.containerItem}
								onClick={() => {
									setSelectedItems([]);
									handleCheckboxChange(item);
								}}
							>
								<Text className={classes.textOption}>
									{t(item.label)}
								</Text>
								{selectedItems.some(
									(selectedItem) =>
										selectedItem.value === item.value
								) && (
									<Image
										className={classes.iconCheck}
										src={CheckIcon}
									/>
								)}
							</Box>
						))
					) : (
						<Text className={classes.emptyItemsText}>
							{t('global.noData')}
						</Text>
					)}
				</Stack>
				<Box
					className={classes.containerCustomDatepicker}
				>
					<Box
						className={
							classes.containerCustomLabelDatepicker
						}
						onClick={() => {
							setSelectedItems([]);
							handleCheckboxDateRangeChange({
								label: 'custom',
								value: 'custom',
							});
						}}
					>
						<Text className={classes.textOption}>
							{t('global.custom')}
						</Text>
						{selectedItems.some(
							(selectedItem) =>
								selectedItem.label === 'custom'
						) && (
							<Image
								className={classes.iconCheck}
								src={CheckIcon}
							/>
						)}
					</Box>
					<DatePickerInput
						data-testid="DPk61R0_ZHjdHi6B-date"
						type="range"
						valueFormat="DD/MM/YYYY"
						placeholder={t('placeholders.selectDate')}
						value={rangeDate}
						leftSection={icon}
						popoverProps={{ withinPortal: false }}
						classNames={{
							input: classes.datepicker,
							placeholder: classes.datepickerPlaceholder,
						}}
						onChange={(date) => {
							const updatedDate = [
								dayjs(date[0]).format('YYYY-MM-DD'),
								date[1]
									? dayjs(date[1])
											.add(1, 'day')
											.format('YYYY-MM-DD')
									: null,
							];
							setFormatedDate(updatedDate);
							setRangeDate(date);
							setSelectedItems([
								{
									label: 'custom',
									value: date,
								},
							]);
						}}
						getDayProps={getDayProps}
					/>
				</Box>
				<Box className={classes.containerButton}>
					<Button
						disabled={IS_DISABLED}
						onClick={handleApply}
						size={isBigScreen ? 'sm' : 'xs'}
						fullWidth
						data-testid="APPk61R0_ZHjdHi6B-button"
					>
						{t('global.apply')}
					</Button>
				</Box>
				<Box className={classes.containerResetButton}>
					<Text
						onClick={() => {
							const body = [
								{ label: 'Date filter', value: dayjs() },
							];
							setRangeDate([null, null]);
							setSelectedItems(body);
							setAppliedItems(body);
							setAppliedRangeDate(rangeDate);
							props.onApply(body);
						}}
						className={classes.textClearSelected}
					>
						{t('global.clear')}
					</Text>
				</Box>
			</Popover.Dropdown>
		</Popover>
	);
};
